// The data sources and pure view models of the proxy identity screens (SCR-484 sign-in status,
// SCR-485 keys and people, ADR-087). The screens are audience-neutral: the customer admin uses
// them in the proxy's Admin area with proxySource (API-486, served locally by nexus-web), and
// AllCode staff use the same screens as the staff twin with staffSource (API-490, the identity
// mirror and the take-away-only command queue). Nothing here ever holds a key, a hash or an IdP
// client secret beyond the one write that sends it (S2): keys are spoken of by name and hint.
//
// Both shells consume this library as source over the workspace's one preact (ADR-101), so the
// screens import preact directly; the bindUi indirection of feature 9 is gone.
import type { ApiClient as AdminClient } from "../../api/client";
import { personName } from "../../primitives/person-name";

// ------------------------------------------------------------------------------------ the model

export type Audience = "customer" | "staff";
export type PersonStatus = "pending" | "active" | "disabled" | "erased";
export type CommandKind = "revoke_key" | "disable_user" | "end_sessions";
export type CommandStatus = "queued" | "applied" | "refused" | "expired" | "not_found";
export type ReasonCode = "lost_device" | "offboarding" | "suspected_leak" | "customer_request" | "other";

export const REASON_CODES: readonly ReasonCode[] = ["lost_device", "offboarding", "suspected_leak", "customer_request", "other"];
export const REASON_CODE_LABELS: Readonly<Record<ReasonCode, string>> = {
  lost_device: "Lost device",
  offboarding: "Offboarding",
  suspected_leak: "Suspected leak",
  customer_request: "Customer request",
  other: "Other",
};
export const COMMAND_LABELS: Readonly<Record<CommandKind, string>> = {
  revoke_key: "Revoke key",
  disable_user: "Disable person",
  end_sessions: "End sessions",
};
export const COMMAND_STATUS_LABELS: Readonly<Record<CommandStatus, string>> = {
  queued: "Queued",
  applied: "Applied",
  refused: "Refused by the proxy",
  expired: "Expired",
  not_found: "Not found on the proxy",
};
export const STATUS_LABELS: Readonly<Record<PersonStatus, string>> = {
  pending: "Waiting for approval",
  active: "Active",
  disabled: "Disabled",
  erased: "Erased",
};

/** The staff reason's bounds (decision 1): shown to the customer in their audit. */
export const MIN_REASON = 10;
export const MAX_REASON = 500;
/** How old the staff mirror may be before the screen warns (SCR-485, alert identity-report-stale). */
export const STALE_AFTER_MS = 30 * 60 * 1000;
/** How close the SP certificate's expiry is before SCR-484 warns. */
export const CERTIFICATE_WARN_DAYS = 30;

/** A key whose person has no name or email: its user id is a uuid, which is never shown (no-uuids rule). */
export const UNKNOWN_OWNER = "Unknown person";

/** One key, the same for both audiences. lastUsedDay is null for a key never used. */
export interface KeyItem {
  keyId: string;
  userId: string;
  owner: string;
  ownerEmail: string;
  name: string;
  label: string;
  client: string;
  deviceName: string;
  source: string;
  createdAt: string;
  lastUsedDay: string | null;
  expiresAt: string | null;
  hint: string;
  legacy: boolean;
  revokedAt: string | null;
  revokedBy: string | null;
}

/** One person, the same for both audiences. lastSignIn is a time (proxy) or a day (mirror). */
export interface Person {
  userId: string;
  externalId: string;
  email: string;
  displayName: string;
  teamExternalId: string;
  teamPinned: boolean;
  proxyAdmin: boolean;
  canReadPrompts: boolean;
  status: PersonStatus;
  statusReason: string;
  signInVia: string;
  lastSignIn: string | null;
  liveKeyCount: number;
  /** The person's pseudonymous billing id (API-584, `p-…`): the proxy's own list only, else null. */
  billingId: string | null;
}

/**
 * Whether the People screen shows billing ids (SCR-587): off in email mode and always for staff;
 * shown in pseudonymous mode; unavailable when the proxy could not read its billing-id key.
 */
export type BillingIds = "off" | "shown" | "unavailable";

/** SCR-587's words. */
export const BILLING_ID_LABEL = "Billing id";
export const BILLING_ID_HINT = "The id AWS Billing shows for this person";
export const BILLING_IDS_UNAVAILABLE = "Billing ids are unavailable — the proxy cannot read its billing-id key";
export const BILLING_ID_SEARCH_OFF = "Search by billing id is off while billing ids are unavailable.";
/** The detail line's help topic (web/home's help registry). */
export const BILLING_ID_HELP = "admin.people.billing-id";

/** API-584's id: `p-` and 24 lower-case hex digits. */
const BILLING_ID = /^p-[0-9a-f]{24}$/;
/** What a search box holds when it is a billing id, or its start: `p-` then hex only. */
const BILLING_ID_SEARCH = /^p-[0-9a-f]*$/;

/** The search text as a billing id search (trimmed, lower-cased), or null when it is not one. */
export function billingIdSearch(text: string): string | null {
  const needle = text.trim().toLowerCase();
  return BILLING_ID_SEARCH.test(needle) ? needle : null;
}

/** A staff support command (ENT-488) as API-490 lists it, newest first. */
export interface Command {
  id: string;
  kind: CommandKind;
  target: string;
  reasonCode: ReasonCode;
  reason: string;
  issuedAt: string;
  status: CommandStatus;
  ackedAt: string | null;
}

/**
 * ready: the lists are live. waiting: the proxy has no applied settings yet (decision 15).
 * central: sign-in is still served centrally, so the proxy's lists are not yet the source of
 * truth and the screens say so instead of showing them. never-reported: the staff mirror has
 * no report from this proxy yet.
 */
export type Mode = "ready" | "waiting" | "central" | "never-reported";

export interface Snapshot {
  mode: Mode;
  /** When the proxy last reported (staff); null for the proxy's own live lists. */
  reportedAt: string | null;
  keys: KeyItem[];
  people: Person[];
  commands: Command[];
  /** The People screen's billing ids (SCR-587); "off" for keys, email mode and the staff twin. */
  billingIds: BillingIds;
}

export type Failure =
  | "session-ended"
  | "forbidden"
  | "not-found"
  | "last-admin"
  | "conflict"
  | "invalid-reason"
  | "invalid-team"
  | "invalid-value"
  | "not-allowed"
  | "unavailable"
  | "failed";

export type Result<T> = { ok: true; value: T } | { ok: false; failure: Failure };

export type Action =
  | { kind: "revoke"; keyId: string }
  | { kind: "approve" | "reject" | "enable" | "disable" | "end-sessions"; userId: string }
  | { kind: "team"; userId: string; teamExternalId: string; pinned: boolean }
  | { kind: "admin"; userId: string; on: boolean }
  | { kind: "prompt-reader"; userId: string; on: boolean };

export type ActionKind = Action["kind"];

/** The staff reason every staff command carries (decisions 1 and 13). */
export interface StaffReason {
  code: ReasonCode;
  text: string;
}

/** Which list a screen of SCR-485 shows. */
export type IdentityScope = "keys" | "people";

/** What a screen of SCR-485 reads and changes, whoever is looking. */
export interface IdentitySource {
  readonly audience: Audience;
  load(scope: IdentityScope): Promise<Result<Snapshot>>;
  /** A customer action applies at once; a staff action queues a command and needs a reason. */
  act(action: Action, reason: StaffReason | null): Promise<Result<null>>;
  /** The invites of SCR-580/581 (API-575), when this source has them. */
  readonly invites?: InviteSource;
}

// ------------------------------------------------------------------------- invites (API-575)

export type InviteStatus = "open" | "expired" | "revoked" | "redeemed";
export type InviteOutcome = "created" | "invited_already" | "already_member" | "waiting_for_approval" | "disabled" | "invalid_email" | "no_email_method";
export type DeliveryOutcome = "sent" | "failed" | "not_configured";

export const INVITE_STATUSES: readonly InviteStatus[] = ["open", "expired", "revoked", "redeemed"];
export const INVITE_OUTCOMES: readonly InviteOutcome[] = ["created", "invited_already", "already_member", "waiting_for_approval", "disabled", "invalid_email", "no_email_method"];
/** API-575's bounds: people per request, the lifetime in days and the note's length. */
export const MAX_INVITES_PER_REQUEST = 100;
export const MIN_LIFETIME_DAYS = 1;
export const MAX_LIFETIME_DAYS = 90;
export const DEFAULT_LIFETIME_DAYS = 14;
export const MAX_NOTE = 500;

/** REQ-217: each stage a time (signedIn, firstKey), a day (firstRequestDay) or null while pending. */
export interface Stages {
  invited: string | null;
  signedIn: string | null;
  firstKey: string | null;
  firstRequestDay: string | null;
}

/** The last delivery of an invite's email; `code` is the failure class (I5), "" otherwise. */
export interface Delivery {
  at: string | null;
  outcome: DeliveryOutcome;
  code: string;
}

/** One invite as API-575 lists it. `inviteId` travels in paths only and is never rendered (I1). */
export interface Invite {
  inviteId: string;
  email: string;
  displayName: string;
  teamExternalId: string;
  invitedBy: { kind: "admin" | "support"; displayName: string; email: string };
  invitedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  redeemedAt: string | null;
  status: InviteStatus;
  stages: Stages;
  lastDelivery: Delivery | null;
  link: string;
  /** The server-rendered message, the send answer's text (API-575's list item); null when absent. */
  message: string | null;
}

export interface InviteFilter {
  status: "" | InviteStatus;
  cursor?: string;
}

export interface InvitePage {
  invites: Invite[];
  /** "" on the last page. */
  cursor: string;
}

export interface CreateInvites {
  people: { email: string; displayName?: string }[];
  teamExternalId?: string;
  note?: string;
  lifetimeDays: number;
  send: boolean;
}

/** One address's answer to a create (REQ-215). */
export interface RowResult {
  email: string;
  outcome: InviteOutcome;
  inviteId: string | null;
  link: string | null;
  message: string | null;
  delivery: { outcome: DeliveryOutcome; code: string } | null;
  person: { displayName: string; status: PersonStatus } | null;
}

/** A resend (SMTP) or renewal (link only): the new expiry, the delivery, and the message when the server sends it. */
export interface Resent {
  delivery: Delivery;
  expiresAt: string;
  message: string | null;
}

export type InviteFailure = Failure | "already-redeemed" | "revoked" | "invalid-body" | "not-supported";

/** Why an invites call failed: what the screen says, and the server's status and code for the banner. */
export interface InviteError {
  failure: InviteFailure;
  status: number;
  code: string;
}

export type InviteResult<T> = { ok: true; value: T } | { ok: false; error: InviteError };

/** API-575: list, create, resend and revoke the installation's invites. */
export interface InviteSource {
  list(filter: InviteFilter): Promise<InviteResult<InvitePage>>;
  create(request: CreateInvites): Promise<InviteResult<RowResult[]>>;
  /** Renews the expiry by the invite's lifetime, and emails it when SMTP is set up (API-575, amended 2026-10-09). */
  send(inviteId: string): Promise<InviteResult<Resent>>;
  revoke(inviteId: string): Promise<InviteResult<{ revokedAt: string }>>;
}

/** An error a source call ended with: the HTTP status and the body's code. */
export class IdentityError extends Error {
  override readonly name = "IdentityError";
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string) {
    super(`${status} ${code}`);
    this.status = status;
    this.code = code;
  }
}

/** What went wrong, as the screens tell it. A 401 from either app's client ends the session. */
export function failureOf(error: unknown): Failure {
  if (error instanceof Error && error.name === "SessionEndedError") return "session-ended";
  if (typeof error !== "object" || error === null) return "failed";
  const { status, code } = error as { status?: unknown; code?: unknown };
  switch (status) {
    case 401:
      return "session-ended";
    case 403:
      return "forbidden";
    case 404:
      return "not-found";
    case 409:
      return code === "last_admin" ? "last-admin" : "conflict";
    case 400:
    case 422:
      if (code === "invalid_team") return "invalid-team";
      if (code === "invalid_value") return "invalid-value";
      return "invalid-reason";
    case 503:
      return "unavailable";
    default:
      return "failed";
  }
}

async function attempt<T>(run: () => Promise<T>): Promise<Result<T>> {
  try {
    return { ok: true, value: await run() };
  } catch (error) {
    return { ok: false, failure: failureOf(error) };
  }
}

const INVITE_CODES: Readonly<Record<string, InviteFailure>> = {
  already_redeemed: "already-redeemed",
  revoked: "revoked",
  invalid_body: "invalid-body",
};

/** An invites call's error: API-575's own codes first, then the shared reading of the status. */
export function inviteErrorOf(error: unknown): InviteError {
  const { status, code } = (typeof error === "object" && error !== null ? error : {}) as { status?: unknown; code?: unknown };
  const known = typeof code === "string" ? INVITE_CODES[code] : undefined;
  return { failure: known ?? failureOf(error), status: typeof status === "number" ? status : 0, code: typeof code === "string" ? code : "" };
}

async function inviteAttempt<T>(run: () => Promise<T>): Promise<InviteResult<T>> {
  try {
    return { ok: true, value: await run() };
  } catch (error) {
    return { ok: false, error: inviteErrorOf(error) };
  }
}

// ------------------------------------------------------------------------- proxySource (API-486)

/** web/home's ApiClient: same-origin fetch with the session's CSRF header on unsafe methods. */
export interface ProxyClient {
  fetch(path: string, init?: RequestInit): Promise<Response>;
}

export const PROXY_BASE = "/api/admin/v1";
/** The proxy's own sign-in page, for the admin's test sign-in (decision 8). */
export const TEST_SIGN_IN_PATH = "/login";

async function send(client: ProxyClient, method: string, path: string, body?: unknown): Promise<unknown> {
  const init: RequestInit = { method, headers: { Accept: "application/json" } };
  if (body !== undefined) {
    init.headers = { Accept: "application/json", "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const response = await client.fetch(PROXY_BASE + path, init);
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text === "" ? undefined : (JSON.parse(text) as unknown);
  } catch {
    parsed = undefined;
  }
  if (!response.ok) {
    const code = typeof parsed === "object" && parsed !== null ? (parsed as { code?: unknown }).code : undefined;
    throw new IdentityError(response.status, typeof code === "string" ? code : "");
  }
  return parsed;
}

type Raw = Record<string, unknown>;

const str = (value: unknown): string => (typeof value === "string" ? value : "");
const optStr = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);
const list = (value: unknown): Raw[] => (Array.isArray(value) ? (value as Raw[]) : []);

// The key store writes a never-used key's day as day zero, which reads as 1970-01-01.
const NEVER_DAY = "1970-01-01";

/** The device part of a key name `<device name> · <client>` (decision 12). */
export function deviceOf(name: string): string {
  const at = name.lastIndexOf(" · ");
  return at === -1 ? name : name.slice(0, at);
}

function personStatus(value: unknown): PersonStatus {
  return value === "pending" || value === "disabled" || value === "erased" ? value : "active";
}

const oneOf = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T => (allowed.includes(value as T) ? (value as T) : fallback);
const DELIVERY_OUTCOMES: readonly DeliveryOutcome[] = ["sent", "failed", "not_configured"];
const record = (value: unknown): Raw | null => (typeof value === "object" && value !== null ? (value as Raw) : null);

function deliveryOf(value: unknown): Delivery | null {
  const d = record(value);
  return d === null ? null : { at: optStr(d["at"]), outcome: oneOf(DELIVERY_OUTCOMES, d["outcome"], "not_configured"), code: str(d["code"]) };
}

function proxyInvite(i: Raw): Invite {
  const by = record(i["invitedBy"]) ?? {};
  const stages = record(i["stages"]) ?? {};
  return {
    inviteId: str(i["inviteId"]),
    email: str(i["email"]),
    displayName: str(i["displayName"]),
    teamExternalId: str(i["teamExternalId"]),
    invitedBy: { kind: by["kind"] === "support" ? "support" : "admin", displayName: str(by["displayName"]), email: str(by["email"]) },
    invitedAt: str(i["invitedAt"]),
    expiresAt: str(i["expiresAt"]),
    revokedAt: optStr(i["revokedAt"]),
    redeemedAt: optStr(i["redeemedAt"]),
    status: oneOf(INVITE_STATUSES, i["status"], "open"),
    stages: {
      invited: optStr(stages["invited"]),
      signedIn: optStr(stages["signedIn"]),
      firstKey: optStr(stages["firstKey"]),
      firstRequestDay: optStr(stages["firstRequestDay"]),
    },
    lastDelivery: deliveryOf(i["lastDelivery"]),
    link: str(i["link"]),
    message: optStr(i["message"]),
  };
}

function rowResult(r: Raw): RowResult {
  const delivery = record(r["delivery"]);
  const person = record(r["person"]);
  return {
    email: str(r["email"]),
    outcome: oneOf(INVITE_OUTCOMES, r["outcome"], "invalid_email"),
    inviteId: optStr(r["inviteId"]),
    link: optStr(r["link"]),
    message: optStr(r["message"]),
    delivery: delivery === null ? null : { outcome: oneOf(DELIVERY_OUTCOMES, delivery["outcome"], "not_configured"), code: str(delivery["code"]) },
    person: person === null ? null : { displayName: str(person["displayName"]), status: personStatus(person["status"]) },
  };
}

/** API-575 through nexus-web. */
export function proxyInvites(client: ProxyClient): InviteSource {
  const enc = encodeURIComponent;
  return {
    list: (filter) =>
      inviteAttempt(async () => {
        const query = new URLSearchParams();
        if (filter.status !== "") query.set("status", filter.status);
        if (filter.cursor) query.set("cursor", filter.cursor);
        const qs = query.toString();
        const body = record(await send(client, "GET", qs ? `/invites?${qs}` : "/invites")) ?? {};
        return { invites: list(body["invites"]).map(proxyInvite), cursor: str(body["cursor"]) };
      }),
    create: (request) => inviteAttempt(async () => list(record(await send(client, "POST", "/invites", request))?.["results"]).map(rowResult)),
    send: (inviteId) =>
      inviteAttempt(async () => {
        const body = record(await send(client, "POST", `/invites/${enc(inviteId)}/send`, {})) ?? {};
        return {
          delivery: deliveryOf(body["delivery"]) ?? { at: null, outcome: "not_configured", code: "" },
          expiresAt: str(body["expiresAt"]),
          message: optStr(body["message"]),
        };
      }),
    revoke: (inviteId) => inviteAttempt(async () => ({ revokedAt: str(record(await send(client, "POST", `/invites/${enc(inviteId)}/revoke`, {}))?.["revokedAt"]) })),
  };
}

const NOT_SUPPORTED: InviteError = { failure: "not-supported", status: 501, code: "not_supported" };
const notSupported = async (): Promise<{ ok: false; error: InviteError }> => ({ ok: false, error: NOT_SUPPORTED });

/** The staff twin's invites until the invite_user command lands (TASK-633): every call is refused. */
export const STAFF_INVITES_NOT_SUPPORTED: InviteSource = { list: notSupported, create: notSupported, send: notSupported, revoke: notSupported };

function proxyKey(k: Raw): KeyItem {
  const lastUsed = optStr(k["lastUsedDay"]);
  return {
    keyId: str(k["keyId"]),
    userId: str(k["userId"]),
    owner: str(k["displayName"]) || str(k["email"]) || UNKNOWN_OWNER,
    ownerEmail: str(k["email"]),
    name: str(k["name"]),
    label: str(k["label"]),
    client: str(k["client"]),
    deviceName: str(k["deviceName"]) || deviceOf(str(k["name"])),
    source: str(k["source"]),
    createdAt: str(k["createdAt"]),
    lastUsedDay: lastUsed === NEVER_DAY ? null : lastUsed,
    expiresAt: optStr(k["expiresAt"]),
    hint: str(k["hint"]),
    legacy: k["legacy"] === true,
    revokedAt: optStr(k["revokedAt"]),
    revokedBy: optStr(k["revokedBy"]),
  };
}

function proxyPerson(p: Raw): Person {
  return {
    userId: str(p["userId"]),
    externalId: str(p["externalId"]),
    email: str(p["email"]),
    displayName: str(p["displayName"]),
    teamExternalId: str(p["teamExternalId"]),
    teamPinned: p["teamPinned"] === true,
    proxyAdmin: p["proxyAdmin"] === true,
    canReadPrompts: p["canReadPrompts"] === true,
    status: personStatus(p["status"]),
    statusReason: str(p["statusReason"]),
    signInVia: str(p["signInVia"]),
    lastSignIn: optStr(p["lastSignInAt"]),
    liveKeyCount: typeof p["liveKeyCount"] === "number" ? p["liveKeyCount"] : 0,
    billingId: typeof p["billingId"] === "string" && BILLING_ID.test(p["billingId"]) ? p["billingId"] : null,
  };
}

/** The proxy's people and what its list said of billing ids: unavailable when any page did. */
function peopleIds(people: Person[], unavailable: boolean): { people: Person[]; billingIds: BillingIds } {
  if (unavailable) return { people: people.map((p) => ({ ...p, billingId: null })), billingIds: "unavailable" };
  return { people, billingIds: people.some((p) => p.billingId !== null) ? "shown" : "off" };
}

/** One sign-in method's standing (SCR-484). */
export interface MethodStatus {
  id: string;
  label: string;
  kind: string;
  type: string;
  working: boolean;
  reason: string;
  entityId: string;
  acs: string;
  redirectUri: string;
  /** Whether the method takes a client secret (OIDC); the value is never read back. */
  takesSecret: boolean;
}

export interface TokenStatus {
  id: string;
  label: string;
  issuer: string;
  jwksUri: string;
  mode: string;
}

export interface SignInStatus {
  loaded: boolean;
  servedBy: string;
  appliedAt: string | null;
  checkedAt: string;
  spCertificateNotAfter: string | null;
  methods: MethodStatus[];
  trustedTokens: TokenStatus[];
}

function signInStatusOf(raw: unknown): SignInStatus {
  const s = (typeof raw === "object" && raw !== null ? raw : {}) as Raw;
  return {
    loaded: s["loaded"] === true,
    servedBy: str(s["servedBy"]),
    appliedAt: optStr(s["appliedAt"]),
    checkedAt: str(s["checkedAt"]),
    spCertificateNotAfter: optStr(s["spCertificateNotAfter"]),
    methods: list(s["methods"]).map((m) => ({
      id: str(m["id"]),
      label: str(m["label"]),
      kind: str(m["kind"]),
      type: str(m["type"]),
      working: m["working"] === true,
      reason: str(m["reason"]),
      entityId: str(m["entityId"]),
      acs: str(m["acs"]),
      redirectUri: str(m["redirectUri"]),
      takesSecret: str(m["secretName"]) !== "",
    })),
    trustedTokens: list(s["trustedTokens"]).map((t) => ({
      id: str(t["id"]),
      label: str(t["label"]),
      issuer: str(t["issuer"]),
      jwksUri: str(t["jwksUri"]),
      mode: str(t["mode"]),
    })),
  };
}

function modeOf(status: SignInStatus): Mode {
  if (!status.loaded) return "waiting";
  return status.servedBy === "central" ? "central" : "ready";
}

/** SCR-484's reads and writes: the customer admin's only, on the proxy. */
export interface SignInSource {
  status(): Promise<Result<SignInStatus>>;
  /** Write-only: the value goes to the proxy account's Secrets Manager and is never read back. */
  putSecret(providerId: string, value: string): Promise<Result<null>>;
  /** Record that the admin's test sign-in through the provider worked (decision 8). */
  confirm(providerId: string): Promise<Result<string>>;
  metadataHref(providerId: string): string;
}

export type ProxySource = IdentitySource & SignInSource & { readonly invites: InviteSource };

// A bound on the people pages one load follows, so a broken cursor cannot loop for ever.
const MAX_PAGES = 100;

const PERSON_ROUTES: Readonly<Record<"approve" | "reject" | "enable" | "disable" | "end-sessions", string>> = {
  approve: "approve",
  reject: "reject",
  enable: "enable",
  disable: "disable",
  "end-sessions": "end-sessions",
};

/** API-486 through nexus-web: the customer admin's own proxy, served from its key store. */
export function proxySource(client: ProxyClient): ProxySource {
  const enc = encodeURIComponent;
  const status = async (): Promise<SignInStatus> => signInStatusOf(await send(client, "GET", "/sign-in/status"));

  const keys = async (): Promise<KeyItem[]> => list(((await send(client, "GET", "/keys")) as Raw | undefined)?.["keys"]).map(proxyKey);

  const people = async (): Promise<{ people: Person[]; billingIds: BillingIds }> => {
    const all: Person[] = [];
    let cursor = "";
    let unavailable = false;
    for (let page = 0; page < MAX_PAGES; page++) {
      const body = ((await send(client, "GET", cursor ? `/people?cursor=${enc(cursor)}` : "/people")) ?? {}) as Raw;
      all.push(...list(body["people"]).map(proxyPerson));
      unavailable ||= body["billingIdUnavailable"] === true;
      cursor = str(body["cursor"]);
      if (cursor === "") break;
    }
    return peopleIds(all, unavailable);
  };

  const path = (action: Action): { method: string; path: string; body?: unknown } => {
    switch (action.kind) {
      case "revoke":
        return { method: "DELETE", path: `/keys/${enc(action.keyId)}` };
      case "team":
        return { method: "POST", path: `/people/${enc(action.userId)}/team`, body: { teamExternalId: action.teamExternalId, pinned: action.pinned } };
      case "admin":
        return { method: "POST", path: `/people/${enc(action.userId)}/admin`, body: { admin: action.on } };
      case "prompt-reader":
        return { method: "POST", path: `/people/${enc(action.userId)}/prompt-reader`, body: { on: action.on } };
      default:
        return { method: "POST", path: `/people/${enc(action.userId)}/${PERSON_ROUTES[action.kind]}`, body: {} };
    }
  };

  return {
    audience: "customer",
    invites: proxyInvites(client),
    load: (scope) =>
      attempt(async () => {
        const [s, rows] = await Promise.allSettled([status(), scope === "keys" ? keys() : people()]);
        if (s.status === "rejected") throw s.reason;
        const mode = modeOf(s.value);
        const empty: Snapshot = { mode, reportedAt: null, keys: [], people: [], commands: [], billingIds: "off" };
        if (mode !== "ready") return empty;
        if (rows.status === "rejected") throw rows.reason;
        return scope === "keys" ? { ...empty, keys: rows.value as KeyItem[] } : { ...empty, ...(rows.value as Awaited<ReturnType<typeof people>>) };
      }),
    act: (action, reason) =>
      attempt(async () => {
        const call = path(action);
        // Approve, reject and disable keep an optional reason on the person and the audit item.
        const body = reason && typeof call.body === "object" && call.body !== null && Object.keys(call.body).length === 0 ? { reason: reason.text } : call.body;
        await send(client, call.method, call.path, body);
        return null;
      }),
    status: () => attempt(status),
    putSecret: (providerId, value) =>
      attempt(async () => {
        await send(client, "PUT", `/sign-in/providers/${enc(providerId)}/secret`, { value });
        return null;
      }),
    confirm: (providerId) =>
      attempt(async () => str(((await send(client, "POST", "/sign-in/confirm", { providerId })) as Raw | undefined)?.["confirmedAt"])),
    metadataHref: (providerId) => `${PROXY_BASE}/sign-in/providers/${enc(providerId)}/sp-metadata`,
  };
}

// ------------------------------------------------------------------------- staffSource (API-490)

const STAFF_IDENTITIES = "/v1/admin/orgs/{orgId}/installations/{installationId}/identities";
const STAFF_COMMANDS = "/v1/admin/orgs/{orgId}/installations/{installationId}/commands";

// The generated schema (src/api/schema.d.ts) predates API-490, so its typed paths do not name
// these two routes yet. The client's one implementation serves any template, so the staff source
// speaks to it through this untyped view until the schema is regenerated.
interface LooseClient {
  get(path: string, options: { path: Record<string, string> }): Promise<unknown>;
  post(path: string, options: { path: Record<string, string>; body: unknown }): Promise<unknown>;
}

const STAFF_KINDS: Partial<Record<ActionKind, CommandKind>> = {
  revoke: "revoke_key",
  disable: "disable_user",
  "end-sessions": "end_sessions",
};

/** Why a staff reason is refused before it is sent, or null when it is a good one. */
export function reasonProblem(text: string): "too-short" | "too-long" | null {
  const length = [...text.trim()].length;
  if (length < MIN_REASON) return "too-short";
  return length > MAX_REASON ? "too-long" : null;
}

function isLive(key: Pick<KeyItem, "revokedAt" | "expiresAt">, now: Date): boolean {
  return key.revokedAt === null && (key.expiresAt === null || new Date(key.expiresAt).getTime() > now.getTime());
}

function commandStatus(value: unknown): CommandStatus {
  return value === "applied" || value === "refused" || value === "expired" || value === "not_found" ? value : "queued";
}

function commandKind(value: unknown): CommandKind {
  return value === "disable_user" || value === "end_sessions" ? value : "revoke_key";
}

function reasonCode(value: unknown): ReasonCode {
  return REASON_CODES.includes(value as ReasonCode) ? (value as ReasonCode) : "other";
}

function staffSnapshot(raw: unknown, now: Date): Snapshot {
  const body = (typeof raw === "object" && raw !== null ? raw : {}) as Raw;
  const reportedAt = optStr(body["reportedAt"]);
  const users = list(body["users"]);
  const byId = new Map(users.map((u) => [str(u["userId"]), u]));
  const keys: KeyItem[] = list(body["keys"]).map((k) => {
    const owner = byId.get(str(k["userId"])) ?? {};
    return {
      keyId: str(k["keyId"]),
      userId: str(k["userId"]),
      owner: str(owner["displayName"]) || str(owner["email"]) || UNKNOWN_OWNER,
      ownerEmail: str(owner["email"]),
      name: str(k["name"]),
      label: str(k["label"]),
      client: str(k["client"]),
      deviceName: deviceOf(str(k["name"])),
      source: str(k["source"]),
      createdAt: str(k["created"]),
      lastUsedDay: optStr(k["lastUsedDay"]),
      expiresAt: optStr(k["expires"]),
      hint: str(k["hint"]),
      legacy: str(k["source"]) === "legacy",
      revokedAt: optStr(k["revokedAt"]),
      revokedBy: optStr(k["revokedBy"]),
    };
  });
  const people: Person[] = users.map((u) => ({
    userId: str(u["userId"]),
    externalId: str(u["externalId"]),
    email: str(u["email"]),
    displayName: str(u["displayName"]),
    teamExternalId: str(u["teamExternalId"]),
    teamPinned: u["teamPinned"] === true,
    proxyAdmin: u["proxyAdmin"] === true,
    canReadPrompts: u["canReadPrompts"] === true,
    status: personStatus(u["status"]),
    statusReason: str(u["statusReason"]),
    signInVia: str(u["signInVia"]),
    lastSignIn: optStr(u["lastSignInDay"]),
    liveKeyCount: keys.filter((k) => k.userId === str(u["userId"]) && isLive(k, now)).length,
    // The billing admin never sees the mapping (REQ-236), whatever a mirror might carry.
    billingId: null,
  }));
  const commands: Command[] = list(body["commands"]).map((c) => ({
    id: str(c["id"]),
    kind: commandKind(c["kind"]),
    target: str(c["target"]),
    reasonCode: reasonCode(c["reasonCode"]),
    reason: str(c["reason"]),
    issuedAt: str(c["issuedAt"]),
    status: commandStatus(c["status"]),
    ackedAt: optStr(c["ackedAt"]),
  }));
  return { mode: reportedAt === null ? "never-reported" : "ready", reportedAt, keys, people, commands, billingIds: "off" };
}

/**
 * API-490 for AllCode staff: the identity mirror as the proxy last reported it, and the
 * take-away-only commands (revoke_key, disable_user, end_sessions), each with a reason code and
 * the free-text reason the customer reads (decisions 1 and 13). Nothing here grants, enables,
 * approves or changes a setting: those actions are refused before any request.
 */
export function staffSource(client: AdminClient, orgId: string, installationId: string, now: () => Date = () => new Date()): IdentitySource {
  const loose = client as unknown as LooseClient;
  const target = { orgId, installationId };
  return {
    audience: "staff",
    invites: STAFF_INVITES_NOT_SUPPORTED,
    load: () => attempt(async () => staffSnapshot(await loose.get(STAFF_IDENTITIES, { path: target }), now())),
    async act(action, reason) {
      const kind = STAFF_KINDS[action.kind];
      if (!kind) return { ok: false, failure: "not-allowed" };
      if (!reason || reasonProblem(reason.text) !== null || !REASON_CODES.includes(reason.code)) return { ok: false, failure: "invalid-reason" };
      const id = action.kind === "revoke" ? action.keyId : (action as { userId: string }).userId;
      return attempt(async () => {
        await loose.post(STAFF_COMMANDS, { path: target, body: { kind, target: id, reasonCode: reason.code, reason: reason.text.trim() } });
        return null;
      });
    },
  };
}

// ------------------------------------------------------------------------------- view helpers

/** The actions a person's row offers. Staff only ever take access away (decision 1). */
export function personActions(audience: Audience, person: Person): ActionKind[] {
  if (audience === "staff") return person.status === "active" ? ["disable", "end-sessions"] : person.status === "pending" ? ["disable"] : [];
  switch (person.status) {
    case "pending":
      return ["approve", "reject"];
    case "active":
      return ["disable", "end-sessions"];
    case "disabled":
      return ["enable"];
    default:
      return [];
  }
}

export const ACTION_LABELS: Readonly<Record<ActionKind, string>> = {
  revoke: "Revoke",
  approve: "Approve",
  reject: "Reject",
  enable: "Enable",
  disable: "Disable",
  "end-sessions": "End sessions",
  team: "Set team",
  admin: "Admin",
  "prompt-reader": "Reads prompts",
};

/** What happens, said before it does. */
export function actionWarning(audience: Audience, kind: ActionKind): string {
  const queued = audience === "staff" ? " The proxy applies it within a minute of pulling it, and the customer sees your reason." : "";
  switch (kind) {
    case "revoke":
      return `The key stops working within a minute.${queued}`;
    case "disable":
      return `Every key and session of this person ends, and they cannot sign in again until enabled.${queued}`;
    case "end-sessions":
      return `Every web session of this person ends. Their keys keep working.${queued}`;
    case "reject":
      return "They cannot sign in until an admin enables them.";
    case "approve":
      return "They can sign in and set up their machines.";
    default:
      return "Their keys stay revoked; they sign in again to get new ones.";
  }
}

export function isKeyLive(key: KeyItem, now: Date): boolean {
  return isLive(key, now);
}

export function keyTitle(key: Pick<KeyItem, "label" | "name">): string {
  return key.label || key.name;
}

// A person is named by the library's one helper: the display name, else the email, else "Unknown
// user", never the external id (IA-1). People's barrel re-exports it, so the package has it once.
export { personName };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parse(value: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
}

/** A day as "30 Sep 2026", in UTC (decision 21: today is the UTC day); null is "never". */
export function formatDay(value: string | null): string {
  if (value === null || value === "") return "never";
  const date = parse(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** A moment as "30 Sep 2026, 12:05 UTC". */
export function formatWhen(value: string): string {
  const date = parse(value);
  if (Number.isNaN(date.getTime())) return value;
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  return `${formatDay(value)}, ${hh}:${mm} UTC`;
}

/** Who or what ended a key: self, idle (decision 21), replaced, disabled, import, support or an admin. */
export function revokedByLabel(by: string | null): string {
  switch (by) {
    case "self":
      return "by its owner";
    case "idle":
      return "unused for too long";
    case "replaced":
      return "replaced by a newer key";
    case "disabled":
      return "when the person was disabled";
    case "import":
      return "at the import";
    case "support":
      return "by AllCode support";
    case null:
    case "":
      return "";
    default:
      return by.startsWith("admin:") ? "by an admin" : by;
  }
}

/** The key's standing in one word or phrase. */
export function keyStanding(key: KeyItem, now: Date): string {
  if (key.revokedAt !== null) {
    const by = revokedByLabel(key.revokedBy);
    return `Revoked ${formatDay(key.revokedAt)}${by ? ` ${by}` : ""}`;
  }
  return isLive(key, now) ? "In use" : `Expired ${formatDay(key.expiresAt)}`;
}

/** Why a person is in their team (SCR-485 "team (and why)"). */
export function teamWhy(person: Pick<Person, "teamExternalId" | "teamPinned">): string {
  if (person.teamExternalId === "") return "No team";
  return person.teamPinned ? "pinned by an admin" : "from their last sign-in";
}

/** The staff twin's stamp (SCR-485). */
export function reportedLine(reportedAt: string): string {
  return `As reported by the proxy at ${formatWhen(reportedAt)}`;
}

export function isStale(reportedAt: string | null, now: Date): boolean {
  return reportedAt !== null && now.getTime() - parse(reportedAt).getTime() > STALE_AFTER_MS;
}

/** The newest command for a key or person (the list is newest first), for the status column. */
export function latestCommand(commands: readonly Command[], target: string): Command | null {
  return commands.find((command) => command.target === target) ?? null;
}

/** The SP certificate line of SCR-484 and whether it warns (decision 20: not rotated automatically). */
export function certificateNote(notAfter: string | null, now: Date): { text: string; warn: boolean } | null {
  if (notAfter === null) return null;
  const days = Math.floor((parse(notAfter).getTime() - now.getTime()) / 86_400_000);
  if (days < 0) return { text: `The SAML certificate expired on ${formatDay(notAfter)}.`, warn: true };
  if (days <= CERTIFICATE_WARN_DAYS) return { text: `The SAML certificate expires in ${days} days, on ${formatDay(notAfter)}.`, warn: true };
  return { text: `The SAML certificate expires on ${formatDay(notAfter)}.`, warn: false };
}

export interface KeyFilter {
  person: string;
  show: "all" | "live" | "revoked";
}

export interface PeopleFilter {
  person: string;
  team: string;
  status: "" | PersonStatus;
}

const matches = (needle: string, ...hay: string[]): boolean => {
  const n = needle.trim().toLowerCase();
  return n === "" || hay.some((value) => value.toLowerCase().includes(n));
};

export function filterKeys(keys: readonly KeyItem[], filter: KeyFilter, now: Date): KeyItem[] {
  return keys.filter(
    (key) =>
      matches(filter.person, key.owner, key.ownerEmail) &&
      (filter.show === "all" || (filter.show === "live") === isLive(key, now)),
  );
}

/** A billing id search (FLOW-054) finds the id it names or starts; any other text, the person's names. */
function matchesPerson(text: string, id: string | null, person: Person): boolean {
  if (id !== null && person.billingId?.startsWith(id)) return true;
  return matches(text, person.displayName, person.email, person.externalId);
}

export function filterPeople(people: readonly Person[], filter: PeopleFilter): Person[] {
  const id = billingIdSearch(filter.person);
  return people.filter(
    (person) =>
      matchesPerson(filter.person, id, person) &&
      (filter.team === "" || person.teamExternalId === filter.team) &&
      (filter.status === "" || person.status === filter.status),
  );
}

/** Every team named in the list, for the team filter. */
export function teamsOf(people: readonly Person[]): string[] {
  return [...new Set(people.map((p) => p.teamExternalId).filter((t) => t !== ""))].sort();
}

// A cell a spreadsheet would run as a formula is prefixed with a quote, so an exported name
// like "=HYPERLINK(...)" stays text.
function csvCell(value: string | number | boolean): string {
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(header: readonly string[], rows: readonly (readonly (string | number | boolean)[])[]): string {
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

/** The keys as CSV: what the table shows, never a hash or a key (S2). */
export function keysCsv(keys: readonly KeyItem[], now: Date): string {
  return toCsv(
    ["person", "email", "key", "client", "source", "hint", "created", "last_used", "expires", "status"],
    keys.map((k) => [k.owner, k.ownerEmail, keyTitle(k), k.client, k.source, k.hint, k.createdAt, k.lastUsedDay ?? "", k.expiresAt ?? "", keyStanding(k, now)]),
  );
}

export function peopleCsv(people: readonly Person[]): string {
  return toCsv(
    // No external_id column: an OIDC, Cognito or trusted-token person's is `<issuer hash>:<sub uuid>` (IA-1).
    ["name", "email", "status", "team", "team_pinned", "proxy_admin", "can_read_prompts", "last_sign_in", "live_keys"],
    people.map((p) => [personName(p), p.email, p.status, p.teamExternalId, p.teamPinned, p.proxyAdmin, p.canReadPrompts, p.lastSignIn ?? "", p.liveKeyCount]),
  );
}

/** What a failed load or action says. */
export function failureMessage(failure: Failure, audience: Audience): string {
  switch (failure) {
    case "session-ended":
      return "Your session ended. Sign in again to continue.";
    case "forbidden":
      return audience === "staff" ? "Your role cannot see this installation's people and keys." : "Only this proxy's admins can open this page.";
    case "not-found":
      return "That is no longer here. Reload to see the current list.";
    case "last-admin":
      return "This is the last admin. Make someone else an admin first.";
    case "conflict":
      return "That changed in the meantime. Reload to see the current state.";
    case "invalid-reason":
      return `Give a reason of ${MIN_REASON} to ${MAX_REASON} characters.`;
    case "invalid-team":
      return "That is not a team id this proxy accepts.";
    case "invalid-value":
      return "That value was refused. Paste the secret again.";
    case "not-allowed":
      return "AllCode support can only take access away.";
    case "unavailable":
      return "The store did not answer. Try again in a minute.";
    default:
      return "Something went wrong. Try again.";
  }
}
